import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelectBusca } from "@/components/padroes";
import { api } from "@/lib/api";
import { formatCents, formatMesRef, parseReaisInput } from "@/lib/format";
import { ROTULO_CATEGORIA, type CategoriaEmprestimo, type ModalidadeEmprestimo } from "@/data/folha";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import type { User } from "@/data/mock";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const competenciaAtual = () => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
};

/** Mesma divisão do backend: a última parcela absorve o resto. */
function preverParcelas(totalCents: number, n: number) {
  if (totalCents <= 0 || n < 1) return [];
  const base = Math.floor(totalCents / n);
  return Array.from({ length: n }, (_, i) =>
    i === n - 1 ? totalCents - base * (n - 1) : base,
  );
}

function somarCompetencia(competencia: string, meses: number) {
  const [ano, mes] = competencia.split("-").map(Number);
  const total = (ano * 12 + (mes - 1)) + meses;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  usuarios: User[];
  onCriado: () => void;
}

export const NovoEmprestimoModal = ({ open, onClose, usuarios, onCriado }: Props) => {
  const [colaboradorId, setColaboradorId] = useState("");
  const [valor, setValor] = useState("");
  const [categoria, setCategoria] = useState<CategoriaEmprestimo>("emprestimo");
  const [modalidade, setModalidade] = useState<ModalidadeEmprestimo>("parcelado_salario");
  const nomeBonificacao = useNomeBonificacao();
  const [parcelas, setParcelas] = useState("6");
  const [competenciaInicial, setCompetenciaInicial] = useState(competenciaAtual());
  const [descricao, setDescricao] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) return;
    setColaboradorId("");
    setValor("");
    setCategoria("emprestimo");
    setModalidade("parcelado_salario");
    setParcelas("6");
    setCompetenciaInicial(competenciaAtual());
    setDescricao("");
  }, [open]);

  const previa = useMemo(() => {
    if (modalidade !== "parcelado_salario") return null;
    let cents: number;
    try {
      cents = Math.round(parseReaisInput(valor) * 100);
    } catch {
      return null;
    }
    const n = Number(parcelas);
    if (!cents || !Number.isInteger(n) || n < 1) return null;
    return preverParcelas(cents, n);
  }, [valor, parcelas, modalidade]);

  const salvar = async () => {
    if (!colaboradorId) {
      toast.error("Selecione o colaborador");
      return;
    }

    let valorReais: number;
    try {
      valorReais = parseReaisInput(valor);
    } catch {
      toast.error("Valor inválido. Use apenas números, ex: 3000,00");
      return;
    }
    if (valorReais <= 0) {
      toast.error("O valor deve ser maior que zero");
      return;
    }

    setSalvando(true);
    try {
      await api.post("/emprestimos", {
        colaboradorId,
        valorTotal: valorReais,
        categoria,
        modalidade,
        ...(modalidade === "parcelado_salario"
          ? { numeroParcelas: Number(parcelas), competenciaInicial }
          : {}),
        ...(descricao.trim() ? { descricao: descricao.trim() } : {}),
      });
      toast.success(`${ROTULO_CATEGORIA[categoria]} registrado`);
      onCriado();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao registrar");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Novo empréstimo ou desconto</DialogTitle>
          <DialogDescription>
            Parcelado: desconta da folha de pagamento mês a mês. Na bonificação: desconta o saldo inteiro da
            próxima {nomeBonificacao.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Colaborador</Label>
            <SelectBusca
              value={colaboradorId}
              onChange={setColaboradorId}
              placeholder="Selecione o colaborador"
              placeholderBusca="Buscar por nome ou e-mail..."
              vazio="Nenhum colaborador encontrado."
              opcoes={usuarios.map((u) => ({ value: u.id, label: u.name, detalhe: u.email }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={categoria} onValueChange={(v) => setCategoria(v as CategoriaEmprestimo)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ROTULO_CATEGORIA) as CategoriaEmprestimo[]).map((c) => (
                  <SelectItem key={c} value={c}>
                    {ROTULO_CATEGORIA[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="emp-valor">Valor (R$)</Label>
            <Input
              id="emp-valor"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              inputMode="decimal"
              placeholder="3.000,00"
            />
          </div>

          <div className="space-y-2">
            <Label>Como será pago</Label>
            <RadioGroup
              value={modalidade}
              onValueChange={(v) => setModalidade(v as ModalidadeEmprestimo)}
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem value="parcelado_salario" id="mod-parcelado" className="mt-0.5" />
                <Label htmlFor="mod-parcelado" className="font-normal">
                  Descontado do salário mês a mês
                  <span className="block text-xs text-muted-foreground">
                    Gera o cronograma e abate na folha de cada competência
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="bonificacao_metas" id="mod-bonificacao" className="mt-0.5" />
                <Label htmlFor="mod-bonificacao" className="font-normal">
                  Descontado da {nomeBonificacao.toLowerCase()}
                  <span className="block text-xs text-muted-foreground">
                    Sem parcelas: a próxima bonificação desconta o saldo inteiro; o que não couber fica para a
                    seguinte. Dá para passar o saldo para parcelas no salário depois.
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {modalidade === "parcelado_salario" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="emp-parcelas">Nº de parcelas</Label>
                <Input
                  id="emp-parcelas"
                  type="number"
                  min={1}
                  max={120}
                  value={parcelas}
                  onChange={(e) => setParcelas(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="emp-inicio">Começa em</Label>
                <Input
                  id="emp-inicio"
                  type="month"
                  value={competenciaInicial}
                  onChange={(e) => setCompetenciaInicial(e.target.value)}
                />
              </div>
            </div>
          )}

          {previa && previa.length > 0 && (
            <div className="rounded-md border bg-muted/30 p-3 text-xs">
              <p className="font-medium">
                {previa.length}x de {formatCents(previa[0])}
                {previa[previa.length - 1] !== previa[0] && (
                  <> — última de {formatCents(previa[previa.length - 1])}</>
                )}
              </p>
              <p className="mt-0.5 text-muted-foreground">
                De {formatMesRef(competenciaInicial)} a{" "}
                {formatMesRef(somarCompetencia(competenciaInicial, previa.length - 1))}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="emp-descricao">Observação (opcional)</Label>
            <Input
              id="emp-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              maxLength={300}
              placeholder="Ex: acordado em reunião"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} disabled={salvando}>
            {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
